import { Check, SlidersHorizontal } from "@digital-twins/design-system/icons";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

import type {
  QuestionExposureMoveKey,
  QuestionExposureSettings,
  QuestionLogicKind,
  QuestionSettingsLogicItem,
} from "./questionSettingsTypes";

export interface QuestionSettingsMenuProps {
  questionId: string;
  logicItems: Array<QuestionSettingsLogicItem>;
  randomize?: { isOn: boolean; onToggle: () => void };
  exposure?: QuestionExposureSettings | null;
  disabled?: boolean;
  disabledReason?: string;
}

// `as const` on purpose: `t()` is typed against the en bundle, so a widened
// `string` value would not typecheck as a key.
const LABEL_KEY_BY_KIND = {
  visibility: "questionnaire.logic.askOnlyIf",
  choicesFrom: "questionnaire.logic.reuseOptions",
  routing: "questionnaire.logic.skipEndOrDisqualify",
} as const satisfies Record<QuestionLogicKind, string>;

const TESTID_PREFIX_BY_KIND: Record<QuestionLogicKind, string> = {
  visibility: "questionnaire-visibility-rule",
  choicesFrom: "questionnaire-choices-from",
  routing: "questionnaire-routing-rule",
};

const LABEL_KEY_BY_MOVE_KEY = {
  "reveal-after": "questionnaire.logic.exposureRevealAfter",
  "reveal-before": "questionnaire.logic.exposureRevealBefore",
  "shared-at": "questionnaire.logic.exposureSharedAt",
  "shared-after": "questionnaire.logic.exposureSharedAfter",
} as const satisfies Record<QuestionExposureMoveKey, string>;

/**
 * The card header's settings trigger: one round icon button that opens the
 * logic items (ask only if / reuse options / skip, end or disqualify),
 * randomize and the exposure sub-menu. Replaces the three "add" footer links
 * (D1); the panel owns rule state, this component only renders what it is
 * given and calls back on selection.
 */
export function QuestionSettingsMenu(props: QuestionSettingsMenuProps) {
  const { questionId, logicItems, randomize, exposure, disabled, disabledReason } = props;

  const { t } = useTranslation("app");

  const visibleLogicItems = logicItems.filter((item) => item.isListed);
  const visibleExposureMoves = exposure
    ? exposure.moves.filter((move) => !move.isNoop)
    : [];

  return (
    <DropdownMenu>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full text-muted-foreground/70 hover:bg-muted hover:text-foreground"
                disabled={disabled}
                title={disabled ? disabledReason : undefined}
                aria-label={t("questionnaire.logic.settings")}
                data-testid={`questionnaire-settings-menu-${questionId}`}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            <span className="text-[11px]">{t("questionnaire.logic.settings")}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenuContent align="start">
        <DropdownMenuLabel>{t("questionnaire.logic.settings")}</DropdownMenuLabel>

        {visibleLogicItems.map((item) => {
          const testIdPrefix = TESTID_PREFIX_BY_KIND[item.kind];
          const isDisabled = !item.isAvailable;

          return (
            <DropdownMenuItem
              key={item.kind}
              disabled={isDisabled}
              onSelect={() => item.onSelect()}
              data-testid={`${testIdPrefix}-add-${questionId}`}
            >
              <div className="flex w-full flex-col">
                <span>{t(LABEL_KEY_BY_KIND[item.kind])}</span>
                {isDisabled && item.unavailableReason ? (
                  <span
                    className="text-xs text-muted-foreground"
                    data-testid={`${testIdPrefix}-unavailable-${questionId}`}
                  >
                    {item.unavailableReason}
                  </span>
                ) : null}
              </div>
              {item.isActive ? <Check className="ml-auto h-4 w-4" /> : null}
            </DropdownMenuItem>
          );
        })}

        {randomize ? (
          <>
            <DropdownMenuSeparator />
            {/* A flush row with a trailing switch, not a CheckboxItem: the
                CheckboxItem indent misaligns it with the logic items above.
                The menu stays open so the switch visibly flips. */}
            <DropdownMenuItem
              role="menuitemcheckbox"
              aria-checked={randomize.isOn}
              onSelect={(event) => {
                event.preventDefault();
                randomize.onToggle();
              }}
              data-testid={`questionnaire-settings-randomize-${questionId}`}
            >
              <span className="flex-1">{t("questionnaire.logic.randomizeOrder")}</span>
              <Switch
                checked={randomize.isOn}
                tabIndex={-1}
                aria-hidden="true"
                className="pointer-events-none ml-3"
              />
            </DropdownMenuItem>
          </>
        ) : null}

        {exposure && visibleExposureMoves.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                data-testid={`questionnaire-settings-exposure-${questionId}`}
              >
                {t("questionnaire.logic.exposure")}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {exposure.zoneLabel ? (
                  <DropdownMenuLabel>{exposure.zoneLabel}</DropdownMenuLabel>
                ) : null}
                {visibleExposureMoves.map((move) => (
                  <DropdownMenuItem
                    key={move.key}
                    disabled={move.isBlocked}
                    onSelect={() => exposure.onMove(move.mark, move.stableKey)}
                    data-testid={`questionnaire-settings-exposure-${move.key}-${questionId}`}
                  >
                    <div className="flex w-full flex-col">
                      <span>{t(LABEL_KEY_BY_MOVE_KEY[move.key])}</span>
                      {move.isBlocked ? (
                        <span className="text-xs text-muted-foreground">
                          {t("questionnaire.logic.exposureBlocked")}
                        </span>
                      ) : null}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
